import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductListPageContext } from "@insite/content-library/Pages/ProductListPage";
import { FormFieldStyle, FormInputWrapper } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import { getProp } from "@insite/mobius/utilities";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { CategoryDetailsPageContext } from "@NA/Overrides/Pages/CategoryDetailsPage";
import ADIColors from "@NA/Utils/ADIColors";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

type Props = ReturnType<typeof mapStateToProps> & WidgetProps;

export interface ProductListControlsContainerStyles {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    sortGridItem?: GridItemProps;
    perPageGridItem?: GridItemProps;
    filterTableViewGridItem?: GridItemProps;
    viewSelectGridItem?: GridItemProps;
    singlePagePerPageGridItem?: GridItemProps;
    singlePageSortGridItem?: GridItemProps;
}

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        gap: 0,
        css: css`
            @media (min-width: 375px) and (max-width: 1079px) {
                max-width: inherit;
            }
            > ${GridOffset} {
                border: 1px solid ${getColor("text.disabled")};
                align-items: center;
                padding: 10px 20px;
                margin: 0 10px;
                width: calc(100% - 20px);
                justify-content: space-between;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                align-items: unset;
                                border: none;
                                padding: 0px;
                            `,
                            css`
                                border: none;
                                align-items: unset;
                                padding: 0px;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    countGridItem: {
        width: [12, 12, 2, 2, 4],
    },
    sortGridItem: {
        width: [6, 6, 6, 4, 5],
        css: css`
            > div[class^="Wrapper"] {
                display: flex;
                justify-content: flex-end;
                margin-right: 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                justify-content: flex-start;
                            `,
                            css`
                                justify-content: flex-start;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
                > ${FormFieldStyle} {
                    width: auto;
                    align-items: center;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                css`
                                    justify-content: flex-start;
                                `,
                                css`
                                    justify-content: flex-start;
                                `,
                                null,
                                null,
                                null,
                            ],
                            "max",
                        )}
                    label {
                        text-transform: unset;
                        font-size: ${getProp("theme.typography.headerSecondary.size")};
                        padding: 6px 10px 0 0;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        padding: 9px 4px 0 0;
                                    `,
                                    null,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }
                    > ${FormInputWrapper} {
                        width: auto;
                        select {
                            padding-right: 25px;
                            font-size: ${getProp("theme.typography.headerSecondary.size")};
                            width: auto;
                            margin-right: 10px;
                            border: 1px solid ${ADIColors.gray3};
                            &:focus {
                                box-shadow: none;
                                outline: 1px solid ${getColor("text.link")};
                                outline-offset: -1px;
                                border-radius: 6px;
                                padding: 9px 15px;
                                padding-right: 25px;
                                border: 1px solid transparent;
                            }
                            ${({ theme }: { theme: BaseTheme }) =>
                                breakpointMediaQueries(
                                    theme,
                                    [
                                        css`
                                            width: 130px;
                                        `,
                                        null,
                                        null,
                                        null,
                                        null,
                                    ],
                                    "max",
                                )};
                        }
                        > ${IconWrapper} {
                            padding: 0px;
                        }
                    }
                }
            }
        `,
    },
    singlePageSortGridItem: {
        width: [12, 12, 8, 8, 7],
        css: css`
            > div[class^="Wrapper"] {
                display: flex;
                justify-content: flex-end;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                justify-content: flex-start;
                            `,
                            css`
                                justify-content: flex-start;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
                >${FormFieldStyle} {
                    width: auto;
                    align-items: center;
                    margin-right: 45px;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css`
                                    margin-right: 0px;
                                `,
                                null,
                                null,
                            ],
                            "max",
                        )}
                    label {
                        text-transform: unset;
                        font-size: ${getProp("theme.typography.headerSecondary.size")};
                        padding: 6px 10px 0 0;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        padding: 9px 4px 0 0;
                                    `,
                                    null,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }
                    > ${FormInputWrapper} {
                        width: auto;
                        select {
                            padding-right: 25px;
                            font-size: ${getProp("theme.typography.headerSecondary.size")};
                            width: auto;
                            margin-right: 10px;
                            border: 1px solid ${ADIColors.gray3};
                            &:focus {
                                box-shadow: none;
                                outline: 1px solid ${getColor("text.link")};
                                outline-offset: -1px;
                                border-radius: 6px;
                                padding: 9px 15px;
                                padding-right: 25px;
                                border: 1px solid transparent;
                            }
                        }
                        > ${IconWrapper} {
                            padding: 0px;
                        }
                    }
                }
            }
        `,
    },
    perPageGridItem: {
        width: [6, 6, 2, 3, 2],
        css: css`
            > div[class^="Wrapper"] {
                display: flex;
                justify-content: center;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                justify-content: flex-end;
                            `,
                            css`
                                justify-content: flex-end;
                            `,
                            null,
                            null,
                            null,
                        ],
                        "max",
                    )}
                > ${FormFieldStyle} {
                    width: auto;
                    align-items: center;
                    label {
                        text-transform: unset;
                        font-size: ${getProp("theme.typography.headerSecondary.size")};
                        padding: 6px 10px 0 0;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    null,
                                    css`
                                        padding: 9px 4px 0 0;
                                    `,
                                    null,
                                    null,
                                    null,
                                ],
                                "max",
                            )}
                    }
                    > ${FormInputWrapper} {
                        width: auto;
                        select {
                            padding-right: 30px;
                            font-size: ${getProp("theme.typography.headerSecondary.size")};
                            width: auto;
                            border: 1px solid ${ADIColors.gray3};
                            &:focus {
                                box-shadow: none;
                                outline: 1px solid ${getColor("text.link")};
                                outline-offset: -1px;
                                border-radius: 6px;
                                padding: 9px 15px;
                                padding-right: 30px;
                                border: 1px solid transparent;
                            }
                        }
                    }
                }
            }
        `,
    },
    singlePagePerPageGridItem: {
        width: 0,
    },
    viewSelectGridItem: {
        width: [0, 0, 2, 1, 1],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = controlsContainerStyles;

const ProductListControlsContainer: FC<Props> = ({ id, pagination }) => {
    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    const isSinglePage = Math.floor((pagination.totalItemCount - 1) / pagination.defaultPageSize) + 1 <= 1;

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.countGridItem}>
                <Zone contentId={id} zoneName="Content00" />
            </GridItem>
            <GridItem {...(isSinglePage ? styles.singlePageSortGridItem : styles.sortGridItem)}>
                <Zone contentId={id} zoneName="Content01" />
            </GridItem>
            <GridItem {...(isSinglePage ? styles.singlePagePerPageGridItem : styles.perPageGridItem)}>
                <Zone contentId={id} zoneName="Content02" />
            </GridItem>
            <GridItem {...styles.viewSelectGridItem}>
                <Zone contentId={id} zoneName="Content03" />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListControlsContainer),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        allowedContexts: [ProductListPageContext, CategoryDetailsPageContext],
    },
};

export default widgetModule;
